import { useContext, useState } from "react";
import type { ReactNode, ReactElement, ComponentType } from "react";
import chroma from "chroma-js";
import { Icon } from "../Icons/Icon";
import { Tooltip } from "../Feedback/Tooltip";
import { stringToColor, stringToColorDark } from "./stringToColor";
import { BadgeSizeContext } from "./BadgeSizeContext";
import { useThemeContext } from "../Theme/useThemeContext";
import type { DialogBaseProps } from "../Dialog/DialogBaseProps";
import { DialogMount_ } from "../Controls/Internal/DialogMount";

export type BadgeVariant =
  | "auto"
  | "info"
  | "success"
  | "warning"
  | "amber"
  | "danger"
  | "purple"
  | "neutral"
  | "dark";
type BadgeSize = "default" | "small";
type BadgeShape = "pill" | "square";
type BadgeMaxWidth = "sm" | "md" | "lg";

interface BadgeProps {
  label: string;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: ComponentType<{ className?: string }>;
  tooltip?: ReactNode;
  /**
   * Lets the pointer reach the tooltip's own content, so its text can be
   * selected and copied instead of vanishing when you reach for it. See
   * `Tooltip`'s own `interactive` for what Radix does and does not keep
   * reachable — keyboard users get nothing, so never put a control in here.
   */
  interactive?: boolean;
  /**
   * How long the pointer must rest before the tooltip opens. Badges usually sit
   * shoulder-to-shoulder in a row, where an instant tooltip opens on the way
   * past and covers the badge the user was heading for — pass
   * `DENSE_GRID_TOOLTIP_DELAY_MS` there.
   */
  delay?: number;
  shape?: BadgeShape;
  colorKey?: string;
  /**
   * Caps the badge width and truncates the label with an ellipsis. When set,
   * the full label is shown in a tooltip on hover (unless an explicit `tooltip`
   * is also provided, in which case that wins).
   */
  maxWidth?: BadgeMaxWidth;
  /**
   * Lets a label too long for its container fold onto another line instead of
   * spilling out of it. `w-fit` is a maximum, so a label that already fits is
   * unaffected — it only changes what happens once there isn't room.
   */
  wrap?: boolean;
  /** Lazy dialog — clicking the badge opens the dialog. Works like Button's dialog prop. */
  dialog?: (() => ReactElement<DialogBaseProps>) | undefined;
}

const variantStyles: Record<Exclude<BadgeVariant, "auto">, string> = {
  info: "bg-blue/10 text-info-strong border-blue/20",
  success: "bg-green/10 text-success-strong border-green/20",
  warning: "bg-very-light-orange text-warning-strong border-orange/20",
  // Amber accent with no "warning" semantics — for neutral categorical/stage labels
  // that happen to want a warm colour (e.g. a mid-pipeline recruiting stage).
  amber: "bg-amber-100 text-amber-800 border-amber-300",
  danger: "bg-very-light-red text-danger-strong border-red/20",
  purple: "bg-purple-200/80 text-purple-600/80 border-purple",
  neutral: "bg-muted text-content-secondary border-border",
  dark: "bg-foreground/10 text-content-secondary border-foreground/20",
};

// Badges run a touch more compact.
const sizeStyles: Record<BadgeSize, string> = {
  default: "px-2 py-0.5 text-[11px]",
  small: "px-1.5 py-px text-[10px]",
};

const maxWidthStyles: Record<BadgeMaxWidth, string> = {
  sm: "max-w-[100px]",
  md: "max-w-[160px]",
  lg: "max-w-[220px]",
};

function autoForeground(bg: string): string {
  try {
    return chroma(bg).luminance() > 0.5 ? "#000000" : "#FFFFFF";
  } catch {
    return "#000000";
  }
}

// It renders auto-coloured badges (e.g. company labels) as a light tint of the
// hashed colour with saturated text — matching the fixed variants' colored-text
// look instead of a solid saturated pill.
function autoTintStyle(base: string, isDark: boolean) {
  try {
    const c = chroma(base);
    return isDark
      ? {
          backgroundColor: c.set("hsl.l", 0.2).hex(),
          color: c.set("hsl.l", 0.72).hex(),
          borderColor: c.set("hsl.l", 0.32).hex(),
        }
      : {
          backgroundColor: c.set("hsl.l", 0.92).hex(),
          color: c.set("hsl.l", 0.36).hex(),
          borderColor: c.set("hsl.l", 0.82).hex(),
        };
  } catch {
    return { backgroundColor: base, color: autoForeground(base) };
  }
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function Badge({
  label,
  variant = "auto",
  size,
  icon: IconProp,
  tooltip,
  interactive = false,
  delay = 0,
  shape = "pill",
  colorKey,
  maxWidth,
  wrap = false,
  dialog,
}: BadgeProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const contextSize = useContext(BadgeSizeContext);
  const resolvedSize = size ?? contextSize ?? "default";
  const roundedClass = shape === "pill" ? "rounded-full" : "rounded-md";
  const isAuto = variant === "auto";
  // Reactive theme read — a one-time `classList.contains("dark")` gets captured
  // at first render and never updates, so a badge rendered during a
  // start-dark→swap-light load stays stuck on the dark auto-colours. The theme
  // context re-renders on theme change (and falls back to the DOM in stories).
  const isDark = useThemeContext().resolvedTheme === "dark";
  const colorFn = isDark ? stringToColorDark : stringToColor;
  const autoColor = isAuto ? colorFn(colorKey ?? label) : undefined;
  const clickable = !!dialog;
  const maxWidthClass = maxWidth ? maxWidthStyles[maxWidth] : "";

  const badge = (
    <span
      // `w-fit` keeps the badge sized to its content even when it lives inside
      // a flex column (e.g. <Stack>) where the default `align-items: stretch`
      // would otherwise stretch the badge to the full cell width. Without
      // this, stacked badges grow weirdly long in table cells across Hubble.
      // `align-items` on the parent still works because `width: fit-content`
      // is a maximum, not an absolute — `<Stack align="center">` still centers.
      className={`inline-flex w-fit items-center gap-1 border font-bold ${wrap ? "whitespace-normal" : "whitespace-nowrap"} ${roundedClass} ${sizeStyles[resolvedSize]} ${maxWidthClass} ${isAuto ? "" : variantStyles[variant]} ${clickable ? "cursor-pointer transition-[filter] hover:brightness-90" : ""}`}
      style={isAuto && autoColor ? autoTintStyle(autoColor, isDark) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => setDialogOpen(true) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setDialogOpen(true);
              }
            }
          : undefined
      }
    >
      {IconProp && <Icon icon={IconProp} size="xs" />}
      {maxWidth ? <span className="min-w-0 truncate">{label}</span> : label}
    </span>
  );

  const effectiveTooltip = tooltip ?? (maxWidth ? label : undefined);
  const badgeWithTooltip = effectiveTooltip ? (
    <Tooltip content={effectiveTooltip} interactive={interactive} delay={delay}>
      {badge}
    </Tooltip>
  ) : (
    badge
  );

  if (dialog) {
    return (
      <>
        {badgeWithTooltip}
        <DialogMount_
          dialog={dialog}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      </>
    );
  }

  return badgeWithTooltip;
}
